// ─── AnimationLayer ────────────────────────────────────────────────────────
// Renders a single animation clip into the editor's preview canvas using
// Remotion's <Player>. The Player's playhead is *driven externally* by the
// editor's global `currentTime`; we never let it auto-play. This guarantees
// the animation stays in sync with the rest of the timeline (videos, audio,
// text overlays) even during scrubbing.

import React, { useEffect, useMemo, useRef } from 'react';
import { Player, type PlayerRef } from '@remotion/player';
import { ANIMATION_FPS } from './types';
import { getAnimationTemplate } from './registry';

type Props = {
  templateId: string;
  /** Per-instance prop overrides on top of the template defaults. */
  props: Record<string, unknown>;
  /** Seconds elapsed *within* this clip (0 .. durationSec). */
  localTime: number;
  /** Total clip duration in seconds. */
  durationSec: number;
  /** Composition resolution. Should match the editor's aspect ratio. */
  width: number;
  height: number;
};

export const AnimationLayer: React.FC<Props> = ({
  templateId,
  props,
  localTime,
  durationSec,
  width,
  height,
}) => {
  const template = getAnimationTemplate(templateId);
  const playerRef = useRef<PlayerRef>(null);

  // Merge defaults + overrides. Memoized so the Player doesn't re-mount on
  // every parent re-render — only when the actual prop values change.
  const inputProps = useMemo(() => {
    if (!template) return {};
    return { ...template.defaultProps, ...props };
  }, [template, props]);

  const durationInFrames = Math.max(1, Math.round(durationSec * ANIMATION_FPS));

  // Drive the Player's frame from the editor's playhead.
  useEffect(() => {
    const p = playerRef.current;
    if (!p) return;
    const target = Math.max(
      0,
      Math.min(durationInFrames - 1, Math.round(localTime * ANIMATION_FPS)),
    );
    try {
      p.seekTo(target);
    } catch {
      /* seekTo before mount – ignore. */
    }
  }, [localTime, durationInFrames]);

  if (!template) return null;

  return (
    <Player
      ref={playerRef}
      // The Player API requires a non-generic component type. The runtime
      // shape matches our template's props exactly.
      component={template.Component as React.ComponentType<Record<string, unknown>>}
      inputProps={inputProps}
      durationInFrames={durationInFrames}
      fps={ANIMATION_FPS}
      compositionWidth={width}
      compositionHeight={height}
      controls={false}
      clickToPlay={false}
      doubleClickToFullscreen={false}
      autoPlay={false}
      loop={false}
      style={{ width: '100%', height: '100%' }}
    />
  );
};
